// Lomi Live — ticking countdown to the next exam moment.
//
// Anchored to the SERVER clock (nowUtc) so a student with a skewed device clock
// still sees the real time-to-go — this is a timed competition, the seconds matter.
// One source of truth: the server's `countdown` descriptor drives every variant.

import React, { useEffect, useRef, useState } from 'react';
import './LiveCountdown.css';

export interface LiveCountdownInfo {
  kind: 'pre' | 'next' | 'live' | 'awaiting_reveal' | 'completed';
  subject?: string;
  target_utc?: string;
}

interface Props {
  countdown?: LiveCountdownInfo;
  nowUtc?: string | null; // server clock at fetch time
  onElapsed?: () => void; // fired once when the timer reaches zero
  compact?: boolean; // smaller variant (e.g. dashboard promo)
  stage?: boolean; // dark-glass variant for the immersive lobby
  inline?: boolean; // single tiny line "🍋 Starts in 22:47:13" (dashboard strip)
}

function fmt(ms: number): { d: number; h: number; m: number; s: number } {
  const t = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(t / 86400),
    h: Math.floor((t % 86400) / 3600),
    m: Math.floor((t % 3600) / 60),
    s: t % 60,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

const LiveCountdown: React.FC<Props> = ({ countdown, nowUtc, onElapsed, compact, stage, inline }) => {
  const sx = stage ? ' lc-stage' : '';
  // Offset = server time − client time, measured once. remaining always uses it.
  const offsetRef = useRef<number>(0);
  const firedRef = useRef<boolean>(false);
  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    if (nowUtc) {
      const server = Date.parse(nowUtc);
      if (!Number.isNaN(server)) offsetRef.current = server - Date.now();
    }
    firedRef.current = false;
  }, [nowUtc, countdown?.kind, countdown?.target_utc]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!countdown) return null;
  const { kind, subject, target_utc } = countdown;

  if (kind === 'completed') {
    if (inline)
      return <div className="lc-inline" data-testid="live-countdown">🏆 Champions revealed</div>;
    return (
      <div className={`lc lc-done${compact ? ' lc-compact' : ''}${sx}`} data-testid="live-countdown">
        🏆 <strong>Champions revealed</strong> — see the results
        <div className="lc-am">🏆 አሸናፊዎች ይፋ ሆነዋል — ውጤቶችን ይመልከቱ</div>
      </div>
    );
  }
  if (kind === 'awaiting_reveal') {
    if (inline)
      return <div className="lc-inline" data-testid="live-countdown">✅ Reveal at 1 PM today 🔥</div>;
    return (
      <div className={`lc lc-await${compact ? ' lc-compact' : ''}${sx}`} data-testid="live-countdown">
        ✅ Competition done — <strong>Grand Reveal at 1 PM today</strong> 🔥
        <div className="lc-am">✅ ውድድሩ ተጠናቋል — ዛሬ ከሰዓት 7 ሰዓት ይፋ ይደረጋል 🔥</div>
      </div>
    );
  }
  if (!target_utc) return null;

  const remaining = Date.parse(target_utc) - (now + offsetRef.current);
  if (remaining <= 0 && !firedRef.current) {
    firedRef.current = true;
    onElapsed?.();
  }
  const { d, h, m, s } = fmt(remaining);

  const label =
    kind === 'live'
      ? `🟢 ${subject} is LIVE — closes in`
      : kind === 'next'
      ? `⏭️ ${subject} opens in`
      : `🍋 Lomi Live starts in`;
  const amLabel =
    kind === 'live'
      ? `🟢 ${subject} LIVE — ይዘጋል`
      : kind === 'next'
      ? `⏭️ ${subject} ይጀምራል`
      : `🍋 ሎሚ ላይቭ ይጀምራል`;

  const live = kind === 'live';

  if (inline) {
    const clock = `${d > 0 ? `${d}d ` : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
    const short =
      kind === 'live' ? `🟢 ${subject} —` : kind === 'next' ? `⏭️ ${subject} in` : `🍋 Starts in`;
    return (
      <div className={`lc-inline${live ? ' lc-inline-live' : ''}`} data-testid="live-countdown">
        {short} <b>{clock}</b>
      </div>
    );
  }

  return (
    <div
      className={`lc lc-${kind}${compact ? ' lc-compact' : ''}${live ? ' lc-pulse' : ''}${sx}`}
      data-testid="live-countdown"
    >
      <div className="lc-label">{label}</div>
      <div className="lc-am lc-am-label">{amLabel}</div>
      <div className="lc-clock" data-testid="live-countdown-clock">
        {d > 0 && (
          <span className="lc-unit">
            <b>{d}</b>
            <i>d</i>
          </span>
        )}
        <span className="lc-unit">
          <b>{pad(h)}</b>
          <i>hr</i>
        </span>
        <span className="lc-sep">:</span>
        <span className="lc-unit">
          <b>{pad(m)}</b>
          <i>min</i>
        </span>
        <span className="lc-sep">:</span>
        <span className="lc-unit">
          <b>{pad(s)}</b>
          <i>sec</i>
        </span>
      </div>
      {kind === 'pre' && subject && <div className="lc-foot">{subject} is first</div>}
    </div>
  );
};

export default LiveCountdown;
